// Finance & Books overview: what the ledger says today.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";

import { api } from "../../lib/api";
import { fmtMinor } from "../../lib/books";
import { Empty, Loading, Money, Pill, Section, Stat, Table } from "./shared";

export default function BooksOverview({ onOpenTab }) {
  const [data, setData] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [overview, exc] = await Promise.all([
          api.get("/finance/reports/overview"),
          api.get("/finance/exceptions", { params: { status: "open" } }),
        ]);
        if (!alive) return;
        setData(overview.data);
        setExceptions(exc.data || []);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <Loading label="Reading the ledger..." />;
  if (!data) return <Empty>Nothing has been posted yet.</Empty>;

  const drafts = data.drafts || {};
  const monthly = data.month_to_date || {};

  return (
    <div className="space-y-4" data-testid="books-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Revenue this month" value={fmtMinor(monthly.net_revenue_minor)} testId="books-stat-revenue" />
        <Stat label="Expenses this month" value={fmtMinor(monthly.total_expenses_minor)} testId="books-stat-expenses" />
        <Stat label="Profit this month" value={fmtMinor(monthly.net_income_minor)} testId="books-stat-profit"
              tone={Number(monthly.net_income_minor || 0) < 0 ? "text-rose-600" : "text-emerald-700"} />
        <Stat label="Customers owe you" value={fmtMinor(data.receivables_open_minor)} testId="books-stat-ar" />
        <Stat label="Deposits you hold" value={fmtMinor(data.deposits_held_minor)} testId="books-stat-deposits"
              hint="Money that belongs to renters" />
        <Stat label="Advances held" value={fmtMinor(data.advances_held_minor)} testId="books-stat-advances"
              hint="Paid ahead, not yet charged" />
        <Stat label="Charges ready to post" value={drafts.ready_to_post || 0} testId="books-stat-ready" />
        <Stat label="Charges expected" value={drafts.expected || 0} testId="books-stat-expected" />
      </div>

      {(exceptions.length > 0) && (
        <Section title="Needs a person" description="Nothing here is fixed automatically."
                 testId="books-exceptions-card">
          <div className="space-y-2">
            {exceptions.slice(0, 6).map((row) => (
              <div key={row.id} className="flex items-start gap-2 rounded-xl border border-slate-200 p-3">
                <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${
                  row.severity === "critical" ? "text-rose-600" : "text-amber-600"}`} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">{row.title}</span>
                    <Pill status={row.severity}>{row.severity}</Pill>
                  </div>
                  <p className="text-sm text-slate-600">{row.message}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Cash, bank and e-wallets" testId="books-cash-card"
               actions={<button onClick={() => onOpenTab?.("reports")}
                                className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
                          All reports <ArrowRight className="w-3.5 h-3.5" />
                        </button>}>
        <Table head={["Account", { key: "in", label: "In", align: "right" },
                      { key: "out", label: "Out", align: "right" },
                      { key: "balance", label: "Balance", align: "right" }]}
               empty={(data.cash_accounts || []).length === 0 && <Empty>No cash accounts have moved yet.</Empty>}>
          {(data.cash_accounts || []).map((account) => (
            <tr key={account.account_id} data-testid={`books-cash-${account.code}`}>
              <td className="py-2 pr-3">
                <span className="text-slate-500 mr-2">{account.code}</span>{account.name}
              </td>
              <td className="py-2 pr-3 text-right"><Money minor={account.inflow_minor} /></td>
              <td className="py-2 pr-3 text-right"><Money minor={account.outflow_minor} /></td>
              <td className="py-2 pr-3 text-right font-medium"><Money minor={account.closing_minor} /></td>
            </tr>
          ))}
        </Table>
      </Section>

      <p className="text-xs text-slate-500">
        Figures come only from posted entries. Drafts, quotes and the legacy records archive are never included.
        <Link to="/app/administration" className="ml-1 underline">Open the legacy archive</Link>.
      </p>
    </div>
  );
}
